import { Controller } from "../../lib/stimulus-shim"

/**
 * State Machine UCP - Gere le flow checkout complet sans quitter Rholala
 *
 * States: init -> variant_selection -> shipping_form -> shipping_options -> payment_redirect
 *
 * data-profiles--ucp-checkout-endpoint-value: URL base UCP
 * data-profiles--ucp-checkout-product-id-value: ID produit externe
 * data-profiles--ucp-checkout-module-id-value: ID module Rholala
 * data-profiles--ucp-checkout-show-intermediate-value: boolean
 */
export default class extends Controller {
  static values = {
    endpoint: String,
    productId: String,
    moduleId: Number,
    showIntermediate: { type: Boolean, default: false },
    productTitle: String
  }

  connect() {
    this.modal = null
    this.variants = []
    this.selectedOptions = {}
  }

  async startCheckout(event) {
    event.preventDefault()
    this.trackEvent('click')
    await this.initUcpFlow()
  }

  // === STEP 1: Init UCP handshake ===

  async initUcpFlow() {
    this.openModal()
    this.showSpinner("Verification de la disponibilite...")

    try {
      const response = await this.ucpRequest('POST', '/checkout/init', {
        product_id: this.productIdValue,
        quantity: 1,
        return_url: `${window.location.origin}/checkout/confirm`,
        source: 'rholala'
      })

      this.renderScreen(response)
    } catch (error) {
      this.showError(error.message || "Service temporairement indisponible")
    }
  }

  // === UCP API call with timeout ===

  async ucpRequest(method, path, body = {}) {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 5000)

    try {
      const response = await fetch(`${this.endpointValue}${path}`, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'UCP-Version': '1.0',
          'Accept': 'application/json'
        },
        body: JSON.stringify(body),
        signal: controller.signal
      })

      clearTimeout(timeoutId)

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || `HTTP ${response.status}`)
      }

      return await response.json()
    } catch (error) {
      clearTimeout(timeoutId)

      if (error.name === 'AbortError') {
        throw new Error('Le service ne repond pas. Veuillez reessayer.')
      }
      throw error
    }
  }

  // === Screen router ===

  renderScreen(ucpResponse) {
    this.hideSpinner()

    switch (ucpResponse.screen_type) {
      case 'stock_unavailable':
        this.renderStockUnavailable(ucpResponse)
        break
      case 'variant_selection':
        this.renderVariantSelection(ucpResponse)
        break
      case 'shipping_form':
        this.renderShippingForm(ucpResponse)
        break
      case 'shipping_options':
        this.renderShippingOptions(ucpResponse)
        break
      case 'payment_redirect':
        this.redirectToCheckout(ucpResponse)
        break
      case 'order_confirmed':
        this.renderConfirmation(ucpResponse)
        break
      default:
        this.showError(`Type d'ecran inconnu: ${ucpResponse.screen_type}`)
    }
  }

  // === SCREENS ===

  renderStockUnavailable(data) {
    this.setContent(`
      <div class="text-center py-12">
        <div class="text-6xl mb-4">😔</div>
        <h3 class="text-2xl font-bold mb-2">Produit indisponible</h3>
        <p class="text-base-content/70 mb-6">
          ${data.message || 'Ce produit est actuellement en rupture de stock.'}
        </p>
        <button type="button" class="btn btn-outline" data-action="click->profiles--ucp-checkout#closeModal">
          Fermer
        </button>
      </div>
    `)
  }

  renderVariantSelection(data) {
    const optionsHtml = data.product?.options
      ? this.buildOptionsButtons(data.product.options, data.variants)
      : ''

    this.setContent(`
      <div class="space-y-6">
        <h3 class="text-xl font-bold">Choisissez votre option</h3>
        ${optionsHtml}
        <div class="alert" data-variant-info>
          <div class="flex items-center justify-between w-full">
            <div>
              <span class="font-semibold" data-variant-price>${data.product?.base_price || ''} \u20AC</span>
              <span class="text-sm opacity-70 ml-2" data-variant-stock>En stock</span>
            </div>
          </div>
        </div>
        <button type="button" class="btn btn-primary w-full"
                data-action="click->profiles--ucp-checkout#confirmVariantSelection">
          Continuer
        </button>
      </div>
    `)

    this.variants = data.variants || []
    this.selectedOptions = {}
  }

  buildOptionsButtons(options, variants) {
    return options.map((option, index) => {
      const buttons = option.values.map(value => {
        const hasStock = variants.some(v =>
          v.options && v.options[index] === value && v.available
        )
        return `
          <button type="button"
                  class="btn btn-outline btn-sm flex-1 ${!hasStock ? 'btn-disabled' : ''}"
                  data-option-index="${index}"
                  data-option-value="${value}"
                  ${!hasStock ? 'disabled' : ''}
                  data-action="click->profiles--ucp-checkout#selectOption">
            ${value}
            ${!hasStock ? '<span class="badge badge-error badge-xs ml-1">Rupture</span>' : ''}
          </button>
        `
      }).join('')

      return `
        <div class="form-control">
          <label class="label">
            <span class="label-text font-medium">${option.name}</span>
          </label>
          <div class="flex flex-wrap gap-2">${buttons}</div>
        </div>
      `
    }).join('')
  }

  selectOption(event) {
    const btn = event.currentTarget
    const group = btn.closest('.form-control').querySelector('.flex')
    group.querySelectorAll('.btn').forEach(b => b.classList.remove('btn-active'))
    btn.classList.add('btn-active')

    this.selectedOptions[btn.dataset.optionIndex] = btn.dataset.optionValue

    const variant = this.findVariantByOptions()
    if (variant) {
      const priceEl = this.modal.querySelector('[data-variant-price]')
      const stockEl = this.modal.querySelector('[data-variant-stock]')
      if (priceEl) priceEl.textContent = `${variant.price} \u20AC`
      if (stockEl) {
        stockEl.textContent = variant.available ? 'En stock' : 'Rupture de stock'
        stockEl.classList.toggle('text-error', !variant.available)
      }
    }
  }

  findVariantByOptions() {
    return this.variants.find(v =>
      Object.keys(this.selectedOptions).every(index =>
        v.options && v.options[index] === this.selectedOptions[index]
      )
    )
  }

  async confirmVariantSelection() {
    const variant = this.findVariantByOptions()
    if (!variant) {
      this.showError("Veuillez selectionner toutes les options")
      return
    }
    if (!variant.available) {
      this.showError("Ce produit n'est plus disponible dans cette combinaison")
      return
    }

    this.showSpinner("Chargement...")
    try {
      const response = await this.ucpRequest('POST', '/checkout/select-variant', {
        variant_id: variant.id
      })
      this.renderScreen(response)
    } catch (error) {
      this.showError(error.message)
    }
  }

  renderShippingForm(data) {
    const countries = data.countries || ['FR', 'BE', 'CH', 'LU']

    this.setContent(`
      <div class="space-y-4">
        <h3 class="text-xl font-bold">Adresse de livraison</h3>
        <form id="ucp-shipping-form" class="space-y-4">
          <div class="form-control">
            <label class="label"><span class="label-text">Pays</span></label>
            <select name="country" class="select select-bordered" required>
              ${countries.map(c => `<option value="${c}">${c}</option>`).join('')}
            </select>
          </div>
          <div class="form-control">
            <label class="label"><span class="label-text">Code postal</span></label>
            <input type="text" name="postal_code" class="input input-bordered" placeholder="75001" required>
          </div>
          <div class="form-control">
            <label class="label"><span class="label-text">Ville</span></label>
            <input type="text" name="city" class="input input-bordered" placeholder="Paris" required>
          </div>
          <button type="submit" class="btn btn-primary w-full">Calculer la livraison</button>
        </form>
      </div>
    `)

    this.modal.querySelector('#ucp-shipping-form').addEventListener('submit', async (e) => {
      e.preventDefault()
      const formData = new FormData(e.target)
      this.showSpinner("Calcul des frais de port...")
      try {
        const response = await this.ucpRequest('POST', '/checkout/calculate-shipping', {
          address: Object.fromEntries(formData)
        })
        this.renderScreen(response)
      } catch (error) {
        this.showError(error.message)
      }
    })
  }

  renderShippingOptions(data) {
    const optionsHtml = (data.options || []).map(opt => `
      <div class="card bg-base-100 border border-base-300 hover:border-primary hover:bg-base-200 cursor-pointer transition"
           data-shipping-id="${opt.id}">
        <div class="card-body p-4 flex-row items-center justify-between">
          <div>
            <h4 class="font-semibold">${opt.name}</h4>
            <p class="text-sm opacity-70">${opt.carrier} - ${opt.estimated_days}</p>
          </div>
          <div class="text-xl font-bold text-primary">${opt.price} \u20AC</div>
        </div>
      </div>
    `).join('')

    this.setContent(`
      <div class="space-y-4">
        <h3 class="text-xl font-bold">Mode de livraison</h3>
        <div class="space-y-3">${optionsHtml}</div>
      </div>
    `)

    this.modal.querySelectorAll('[data-shipping-id]').forEach(card => {
      card.addEventListener('click', async () => {
        this.showSpinner("Preparation du paiement...")
        try {
          const response = await this.ucpRequest('POST', '/checkout/finalize', {
            shipping_method_id: card.dataset.shippingId
          })
          this.renderScreen(response)
        } catch (error) {
          this.showError(error.message)
        }
      })
    })
  }

  redirectToCheckout(data) {
    this.trackEvent('checkout_started')
    this.setContent(`
      <div class="text-center py-12">
        <div class="loading loading-spinner loading-lg text-primary mb-4"></div>
        <h3 class="text-xl font-semibold mb-2">Redirection vers le paiement securise</h3>
        <p class="text-sm opacity-70">${data.message || 'Vous allez etre redirige dans quelques instants...'}</p>
      </div>
    `)

    setTimeout(() => {
      window.location.href = data.checkout_url
    }, 1500)
  }

  renderConfirmation(data) {
    this.trackEvent('purchase', {
      order_number: data.order_number,
      order_value: data.total,
      currency: data.currency
    })
    this.setContent(`
      <div class="text-center py-12">
        <div class="text-6xl mb-4">🎉</div>
        <h3 class="text-2xl font-bold mb-2">Commande confirmee !</h3>
        <p class="opacity-70 mb-1">Numero de commande : <strong>${data.order_number}</strong></p>
        <p class="text-sm opacity-60 mb-6">Vous recevrez un email de confirmation</p>
        <button type="button" class="btn btn-primary"
                data-action="click->profiles--ucp-checkout#closeModal">
          Fermer
        </button>
      </div>
    `)
  }

  // === Analytics tracking ===

  trackEvent(eventType, extra = {}) {
    if (!this.moduleIdValue) return

    const body = {
      event_type: eventType,
      module_id: this.moduleIdValue,
      product_title: this.productTitleValue || null,
      product_id: this.productIdValue || null,
      checkout_mode: 'ucp',
      ...extra
    }

    fetch('/api/commerce/track', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true
    }).catch(() => {})
  }

  // === Modal management ===

  openModal() {
    if (!this.modal) {
      this.modal = document.createElement('dialog')
      this.modal.className = 'modal'
      this.modal.innerHTML = `
        <div class="modal-box max-w-2xl">
          <div data-role="spinner" class="hidden"></div>
          <div data-role="error" class="hidden mb-4"></div>
          <div data-role="content"></div>
        </div>
        <form method="dialog" class="modal-backdrop"><button>close</button></form>
      `
      document.body.appendChild(this.modal)
    }
    this.modal.showModal()
  }

  closeModal() {
    if (this.modal) {
      this.modal.close()
    }
  }

  setContent(html) {
    if (!this.modal) return
    const content = this.modal.querySelector('[data-role="content"]')
    const spinner = this.modal.querySelector('[data-role="spinner"]')
    const error = this.modal.querySelector('[data-role="error"]')
    if (content) { content.innerHTML = html; content.classList.remove('hidden') }
    if (spinner) spinner.classList.add('hidden')
    if (error) error.classList.add('hidden')
  }

  showSpinner(message = "Chargement...") {
    if (!this.modal) return
    const spinner = this.modal.querySelector('[data-role="spinner"]')
    const content = this.modal.querySelector('[data-role="content"]')
    if (spinner) {
      spinner.innerHTML = `
        <div class="text-center py-12">
          <div class="loading loading-spinner loading-lg text-primary mb-4"></div>
          <p class="text-sm opacity-70">${message}</p>
        </div>
      `
      spinner.classList.remove('hidden')
    }
    if (content) content.classList.add('hidden')
  }

  hideSpinner() {
    if (!this.modal) return
    const spinner = this.modal.querySelector('[data-role="spinner"]')
    const content = this.modal.querySelector('[data-role="content"]')
    if (spinner) spinner.classList.add('hidden')
    if (content) content.classList.remove('hidden')
  }

  showError(message) {
    if (!this.modal) return
    const error = this.modal.querySelector('[data-role="error"]')
    if (error) {
      error.innerHTML = `
        <div class="alert alert-error">
          <span>${message}</span>
        </div>
      `
      error.classList.remove('hidden')
    }
    this.hideSpinner()
  }

  disconnect() {
    if (this.modal) {
      this.modal.remove()
      this.modal = null
    }
  }
}
